import { forwardRef, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';

// =============================================================================
// Button
// =============================================================================
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'danger';
  fullWidth?: boolean;
}

// forwardRef so callers can move focus to a specific button — e.g. ConfirmDialog
// focusing Cancel on a destructive action so a reflexive Enter can't delete.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'default', fullWidth, className = '', children, ...props },
  ref
) {
  const variantClass =
    variant === 'primary' ? 'aim-button-primary' : variant === 'danger' ? 'aim-button-danger' : '';
  return (
    <button
      ref={ref}
      className={`aim-button ${variantClass} ${fullWidth ? 'w-full' : ''} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
});

// =============================================================================
// Input
// =============================================================================
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, className = '', id, ...props },
  ref
) {
  // Fall back to a generated id: previously this was `id ?? props.name`, so an
  // Input given a label but neither id nor name rendered a <label htmlFor
  //={undefined}> — visually fine, but not associated with the field, so
  // screen readers announced an unlabelled input and clicking the label did
  // nothing. useId guarantees the pairing.
  const generatedId = useId();
  const inputId = id ?? props.name ?? generatedId;
  const errorId = error ? `${inputId}-error` : undefined;
  return (
    <div className="space-y-1">
      {label && (
        <label htmlFor={inputId} className="block text-xs font-bold uppercase tracking-wider text-text-muted">
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        className={`aim-input ${className}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        {...props}
      />
      {error && <p id={errorId} className="text-xs text-danger">{error}</p>}
    </div>
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
}

export function Textarea({ label, className = '', id, ...props }: TextareaProps) {
  const inputId = id ?? props.name;
  return (
    <div className="space-y-1">
      {label && (
        <label htmlFor={inputId} className="block text-xs font-bold uppercase tracking-wider text-text-muted">
          {label}
        </label>
      )}
      <textarea id={inputId} className={`aim-input resize-none ${className}`} {...props} />
    </div>
  );
}

// =============================================================================
// Window (the chrome panel with title bar)
// =============================================================================
interface WindowProps {
  title?: ReactNode;
  toolbar?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Window({ title, toolbar, children, className = '' }: WindowProps) {
  return (
    <div className={`hybrid-card flex flex-col ${className}`}>
      {title && (
        <div className="hybrid-card-header justify-between">
          <span>{title}</span>
          {toolbar && <div className="flex items-center gap-2">{toolbar}</div>}
        </div>
      )}
      <div className="flex-1 min-h-0 flex flex-col">{children}</div>
    </div>
  );
}

// =============================================================================
// Modal
// =============================================================================
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: string; // tailwind class, e.g. 'w-96'
}

/** Focusable descendants, in DOM order, skipping anything disabled or hidden. */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, title, children, width = 'w-[420px]' }: ModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Escape to close, plus a focus TRAP. Without the trap, Tab walks straight
  // out of the dialog and into the page behind it — the modal looks modal but
  // isn't, and a keyboard user ends up operating controls they can't see under
  // the overlay.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const card = cardRef.current;
      if (!card) return;
      const items = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (items.length === 0) {
        // Nothing focusable inside — keep focus on the card rather than
        // letting Tab escape to the page behind.
        e.preventDefault();
        card.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      // Wrap at both ends, and pull focus back in if it somehow escaped.
      if (e.shiftKey && (active === first || !card.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !card.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Move focus into the dialog on open and RESTORE it to the trigger on close,
  // so closing a modal doesn't dump keyboard users back at the top of the page.
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const card = cardRef.current;
    // Prefer the first real control; fall back to the card itself.
    const target = card?.querySelector<HTMLElement>(FOCUSABLE) ?? card;
    target?.focus();
    return () => previous?.focus?.();
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fade-in p-4"
      onClick={onClose}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`${width} max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] flex flex-col animate-slide-up hybrid-card outline-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hybrid-card-header justify-between">
          <span id={titleId}>{title}</span>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-text-muted hover:text-text text-lg leading-none px-1"
          >
            ×
          </button>
        </div>
        {/* Body scrolls when content overflows; structural flex lets the
            window itself stay within viewport bounds. */}
        <div className="bg-surface flex-1 min-h-0 overflow-y-auto">
          <div className="p-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Status dot (online/idle/dnd/offline)
// =============================================================================
export function StatusDot({ status }: { status: 'online' | 'idle' | 'dnd' | 'offline' }) {
  return (
    <span
      className={`status-dot status-${status}`}
      title={status === 'dnd' ? 'Do not disturb' : status}
    />
  );
}

// =============================================================================
// Avatar - colored circle with initials, AIM "buddy" style
// =============================================================================
export function Avatar({
  name,
  url,
  size = 32,
  status,
}: {
  name: string;
  url?: string | null;
  size?: number;
  status?: 'online' | 'idle' | 'dnd' | 'offline';
}) {
  // Pick a deterministic color based on the name
  const hue = hashName(name) % 360;
  const initials = name
    .split(/[\s._-]+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Image load state. We ALWAYS render the initials circle as the base layer
  // and overlay the <img> on top, fading it in once it decodes. This makes the
  // avatar solid in every case:
  //   - while loading  → initials show instantly (no empty-box flash)
  //   - on success     → image covers the initials
  //   - on error       → image stays transparent, initials remain visible
  // (the old code hid a lone <img> on error, leaving a blank box — there was
  // no initials fallback underneath when a url was set).
  //
  // `attempt` drives silent retries for transient failures (network blip,
  // contended connection). On error we remount the <img> with the SAME url —
  // we must NOT append a cache-busting query param: avatar URLs are SigV4
  // presigned (X-Amz-Signature), and any extra query param changes the
  // canonical query string and invalidates the signature, turning a one-off
  // blip into a permanent 403. Bumping `attempt` is used only as the <img>
  // `key`, forcing a fresh element that re-requests the identical (signed) url.
  // `url` is a dep below so a new avatar resets the state cleanly.
  const MAX_RETRIES = 2;
  const [loaded, setLoaded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retryTimer = useRef<number | null>(null);
  useEffect(() => {
    setLoaded(false);
    setAttempt(0);
    return () => {
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
    };
  }, [url]);

  function handleImgError() {
    setLoaded(false);
    if (attempt >= MAX_RETRIES) return; // give up → initials stay visible
    if (retryTimer.current) window.clearTimeout(retryTimer.current);
    // Short backoff, then remount to retry the same signed url.
    retryTimer.current = window.setTimeout(() => setAttempt((a) => a + 1), 300 * (attempt + 1));
  }

  const src = url || null;

  return (
    <div className="relative inline-block shrink-0">
      <div
        className="relative rounded-aim flex items-center justify-center font-bold text-white select-none overflow-hidden"
        style={{
          width: size,
          height: size,
          backgroundColor: `hsl(${hue}, 45%, 50%)`,
          fontSize: size * 0.42,
          textShadow: '0 1px 1px rgba(0,0,0,0.3)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        {!loaded && (initials || '?')}
        {src && (
          <img
            key={attempt}
            src={src}
            alt={name}
            className="absolute inset-0 rounded-aim object-cover select-none"
            style={{
              width: size,
              height: size,
              opacity: loaded ? 1 : 0,
              transition: 'opacity 150ms ease',
            }}
            onLoad={() => setLoaded(true)}
            onError={handleImgError}
          />
        )}
      </div>
      {status && (
        <span
          className={`status-dot status-${status} absolute -bottom-0.5 -right-0.5`}
          style={{ width: Math.max(8, size * 0.28), height: Math.max(8, size * 0.28) }}
        />
      )}
    </div>
  );
}

function hashName(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// =============================================================================
// Spinner
// =============================================================================
export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className="animate-spin"
      style={{ color: 'currentColor' }}
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.2" />
      <path
        fill="currentColor"
        d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2z"
      />
    </svg>
  );
}

// =============================================================================
// Icon — simple SVG wrapper, inline so we don't need a lucide dep
// =============================================================================
export function Icon({ name, size = 16, className = '' }: { name: string; size?: number; className?: string }) {
  const path = ICONS[name];
  if (!path) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {path}
    </svg>
  );
}

const ICONS: Record<string, ReactNode> = {
  send: <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />,
  'chevron-down': <polyline points="6 9 12 15 18 9" />,
  'chevron-up': <polyline points="18 15 12 9 6 15" />,
  'chevron-left': <polyline points="15 18 9 12 15 6" />,
  'chevron-right': <polyline points="9 18 15 12 9 6" />,
  folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
  'folder-plus': (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <line x1="12" y1="11" x2="12" y2="17" />
      <line x1="9" y1="14" x2="15" y2="14" />
    </>
  ),
  paperclip: <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />,
  plus: (
    <g>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </g>
  ),
  logout: (
    <g>
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </g>
  ),
  mic: (
    <g>
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </g>
  ),
  'mic-off': (
    <g>
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6" />
      <path d="M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </g>
  ),
  signal: (
    <g>
      <line x1="5" y1="20" x2="5" y2="16" />
      <line x1="12" y1="20" x2="12" y2="11" />
      <line x1="19" y1="20" x2="19" y2="6" />
    </g>
  ),
  headphones: (
    <g>
      <path d="M3 18v-6a9 9 0 0118 0v6" />
      <path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z" />
    </g>
  ),
  'headphones-off': (
    <g>
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M21 15.5V12a9 9 0 00-13.6-7.74M3.61 6.5A9 9 0 003 12v3.5" />
      <path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z" />
    </g>
  ),
  phone: <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />,
  'phone-off': (
    <g>
      <path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.26a2 2 0 012.11-.45 12.84 12.84 0 002.81.7 2 2 0 011.72 2v3a2 2 0 01-2.18 2A19.79 19.79 0 011.18 5.18 2 2 0 013.17 3h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11" />
      <line x1="23" y1="1" x2="1" y2="23" />
    </g>
  ),
  sun: (
    <g>
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </g>
  ),
  moon: <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />,
  copy: (
    <g>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </g>
  ),
  download: (
    <g>
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </g>
  ),
  speaker: (
    <g>
      <path d="M11 5L6 9H2v6h4l5 4V5z" />
      <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" />
    </g>
  ),
  back: (
    <g>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </g>
  ),
  edit: (
    <g>
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </g>
  ),
  trash: (
    <g>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </g>
  ),
  check: <polyline points="20 6 9 17 4 12" />,
  x: (
    <g>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </g>
  ),
  search: (
    <g>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </g>
  ),
  bell: (
    <g>
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </g>
  ),
  'bell-off': (
    <g>
      <path d="M13.73 21a2 2 0 01-3.46 0" />
      <path d="M18.63 13A17.89 17.89 0 0118 8" />
      <path d="M6.26 6.26A5.86 5.86 0 006 8c0 7-3 9-3 9h14" />
      <path d="M18 8a6 6 0 00-9.33-5" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </g>
  ),
  user: (
    <g>
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </g>
  ),
  upload: (
    <g>
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </g>
  ),
  smartphone: (
    <g>
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </g>
  ),
  monitor: (
    <g>
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </g>
  ),
  eye: (
    <g>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </g>
  ),
  video: (
    <g>
      <path d="M23 7l-7 5 7 5V7z" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </g>
  ),
  'video-off': (
    <g>
      <path d="M16 16v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2m4 0h4a2 2 0 0 1 2 2v2.5l4-3v9" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </g>
  ),
  expand: (
    <g>
      <path d="M15 3h6v6" />
      <path d="M9 21H3v-6" />
      <path d="M21 3l-7 7" />
      <path d="M3 21l7-7" />
    </g>
  ),
  minimize: (
    <g>
      <path d="M4 14h6v6" />
      <path d="M20 10h-6V4" />
      <path d="M14 10l7-7" />
      <path d="M3 21l7-7" />
    </g>
  ),
  'flip-camera': (
    <g>
      <path d="M23 4v6h-6" />
      <path d="M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
      <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
    </g>
  ),
  chat: (
    <g>
      <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
    </g>
  ),
  grid: (
    <g>
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </g>
  ),
  server: (
    <g>
      <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
      <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
      <line x1="6" y1="6" x2="6.01" y2="6" />
      <line x1="6" y1="18" x2="6.01" y2="18" />
    </g>
  ),
  database: (
    <g>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </g>
  ),
  activity: (
    <g>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </g>
  ),
  star: (
    <g>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </g>
  ),
  'star-filled': (
    <g>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill="currentColor" />
    </g>
  ),
  pin: (
    <g>
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
    </g>
  ),
  link: (
    <g>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </g>
  ),
  lock: (
    <g>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </g>
  ),
  'pin-off': (
    <g>
      <path d="M12 17v5" />
      <path d="M15 9.34V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H7.89" />
      <path d="m2 2 20 20" />
      <path d="M9 9v1.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h11" />
    </g>
  ),
  smile: (
    <g>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </g>
  ),
  refresh: (
    <g>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
    </g>
  ),
  'monitor-off': (
    <g>
      <path d="M17 17H4a2 2 0 01-2-2V5c0-1.1.9-2 2-2h2" />
      <path d="M22 15V5a2 2 0 00-2-2H9" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </g>
  ),
  settings: (
    <g>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </g>
  ),
};
